/**
 * Logging utilities for the admin frontend
 *
 * This module provides centralized logging functions to keep component code clean
 * and maintain consistent log formatting across the application.
 */

/**
 * Centralized timestamp function
 */
const getTimestamp = () => new Date().toISOString().split('T')[1].slice(0, -1)

/**
 * Centralized console.log function with timestamp
 */
const logWithTimestamp = (prefix: string, message: string, data?: any) => {
  console.log(`[${getTimestamp()}] ${prefix} - ${message}:`, data)
}

/**
 * Creates a debug logger with a prefix
 * @param prefix - The prefix to add to log messages (e.g., 'PageEditor', 'UI Store')
 */
export const createLogger = (prefix: string) => {
  return (method: string, data?: any) => {
    logWithTimestamp(prefix, method, data)
  }
}

/**
 * Creates a state change logger with a prefix
 * @param prefix - The prefix to add to log messages
 */
export const createStateLogger = (prefix: string) => {
  return (method: string, before: any, after: any) => {
    logWithTimestamp(`📊 ${prefix} State Change`, method, { before, after })
  }
}

/**
 * Component-specific logging functions
 */
export const componentLogs = {
  /**
   * AdminComponentWrapper logging functions
   */
  componentWrapper: {
    clicked: (component: any, target: string, isSelected: boolean) => {
      logWithTimestamp('🔧 AdminComponentWrapper', 'Component wrapper clicked', {
        componentId: component.id,
        componentType: component.type,
        target,
        isSelected
      })
    },
    selectionEmitted: (component: any) => {
      logWithTimestamp('🔧 AdminComponentWrapper', 'Component selection emitted', {
        componentId: component.id,
        componentType: component.type
      })
    },
    mouseEnter: (component: any) => {
      logWithTimestamp('🔧 AdminComponentWrapper', 'Component mouse enter', {
        componentId: component.id,
        componentType: component.type
      })
    },
    mouseLeave: (component: any) => {
      logWithTimestamp('🔧 AdminComponentWrapper', 'Component mouse leave', {
        componentId: component.id,
        componentType: component.type
      })
    },
    editClicked: (component: any) => {
      logWithTimestamp('🔧 AdminComponentWrapper', 'Edit button clicked', {
        componentId: component.id,
        componentType: component.type
      })
    },
    duplicateClicked: (component: any) => {
      logWithTimestamp('🔧 AdminComponentWrapper', 'Duplicate button clicked', {
        componentId: component.id,
        componentType: component.type
      })
    },
    sharedComponentClicked: (component: any) => {
      logWithTimestamp('🔧 AdminComponentWrapper', 'Shared component clicked', {
        componentId: component.id,
        componentType: component.type
      })
    }
  },

  /**
   * DropZone logging functions
   */
  dropZone: {
    dragEnter: (position: number, parentId?: number) => {
      logWithTimestamp('📦 DropZone', 'Drag enter', { position, parentId })
    },
    dragLeave: (position: number, parentId?: number) => {
      logWithTimestamp('📦 DropZone', 'Drag leave', { position, parentId })
    },
    drop: (position: number, parentId?: number) => {
      logWithTimestamp('📦 DropZone', 'Drop', { position, parentId })
    },
    clicked: (position: number, parentId?: number) => {
      logWithTimestamp('📦 DropZone', 'Clicked', { position, parentId })
    }
  }
}

/**
 * Pre-configured loggers for common components
 */
export const loggers = {
  pageEditor: createLogger('📎 PageEditor'),
  uiStore: createLogger('🎯 UI Store'),
  componentWrapper: createLogger('🔧 AdminComponentWrapper'),
  dropZone: createLogger('📦 DropZone'),
  stateChange: createStateLogger('UI Store')
}